import React, { useEffect } from 'react';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import { useTranslation } from 'react-i18next';
import './ExperiencesSantaCatarina.css';

const ExperiencesSantaCatarina: React.FC = () => {
  const { scrollState } = useScrollManager();
  const { setHoveredDestination } = useOverlayManager();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
  }, []);

  // Experience items data with translated content
  const experiences = [
    {
      id: 1,
      image: '/assets/experience-guide-1.png',
      title: t('properties/santa-catarina:experiences.activities.diving.title'),
      location: t('properties/santa-catarina:experiences.activities.diving.location'),
      description: t('properties/santa-catarina:experiences.activities.diving.description')
    },
    {
      id: 2,
      image: '/assets/experience-guide-2.png',
      title: t('properties/santa-catarina:experiences.activities.fortress.title'),
      location: t('properties/santa-catarina:experiences.activities.fortress.location'),
      description: t('properties/santa-catarina:experiences.activities.fortress.description')
    },
    {
      id: 3,
      image: '/assets/experience-guide-3.png',
      title: t('properties/santa-catarina:experiences.activities.hiking.title'),
      location: t('properties/santa-catarina:experiences.activities.hiking.location'),
      description: t('properties/santa-catarina:experiences.activities.hiking.description')
    },
    {
      id: 4,
      image: '/assets/experience-guide-4.png',
      title: t('properties/santa-catarina:experiences.activities.surfing.title'),
      location: t('properties/santa-catarina:experiences.activities.surfing.location'),
      description: t('properties/santa-catarina:experiences.activities.surfing.description')
    },
    {
      id: 5,
      image: '/assets/experience-guide-5.png',
      title: t('properties/santa-catarina:experiences.activities.cachaca.title'),
      location: t('properties/santa-catarina:experiences.activities.cachaca.location'),
      description: t('properties/santa-catarina:experiences.activities.cachaca.description')
    },
    {
      id: 6,
      image: '/assets/experience-guide-6.png',
      title: t('properties/santa-catarina:experiences.activities.cityTour.title'),
      location: t('properties/santa-catarina:experiences.activities.cityTour.location'),
      description: t('properties/santa-catarina:experiences.activities.cityTour.description')
    }
  ];

  // Team members data with translated content
  const teamMembers = [
    {
      id: 1,
      image: '/assets/images/localhost/9fe76396d22e63fa2f0e5f856d1c5e01988dbbb6.png',
      name: t('properties/santa-catarina:experiences.team.members.marinaSilva.name'),
      role: t('properties/santa-catarina:experiences.team.members.marinaSilva.role'),
      description: t('properties/santa-catarina:experiences.team.members.marinaSilva.description')
    },
    {
      id: 2,
      image: '/assets/images/localhost/b23b62a9f96cc5ab8a93da33fe544df6c49884a3.png',
      name: t('properties/santa-catarina:experiences.team.members.joaoSantos.name'),
      role: t('properties/santa-catarina:experiences.team.members.joaoSantos.role'),
      description: t('properties/santa-catarina:experiences.team.members.joaoSantos.description')
    },
    {
      id: 3,
      image: '/assets/images/localhost/0900d5204eb6e5804f3a3f031befd03ac9cff02f.png',
      name: t('properties/santa-catarina:experiences.team.members.isabelaCosta.name'),
      role: t('properties/santa-catarina:experiences.team.members.isabelaCosta.role'),
      description: t('properties/santa-catarina:experiences.team.members.isabelaCosta.description')
    },
    {
      id: 4,
      image: '',
      name: t('properties/santa-catarina:experiences.team.members.pedroOliveira.name'),
      role: t('properties/santa-catarina:experiences.team.members.pedroOliveira.role'),
      description: t('properties/santa-catarina:experiences.team.members.pedroOliveira.description')
    },
    {
      id: 5,
      image: '',
      name: t('properties/santa-catarina:experiences.team.members.anaRodrigues.name'),
      role: t('properties/santa-catarina:experiences.team.members.anaRodrigues.role'),
      description: t('properties/santa-catarina:experiences.team.members.anaRodrigues.description')
    }
  ];

  return (
    <div className="experiences-santa-catarina">
      {/* Navigation - Light variant for Santa Catarina */}
      <UnifiedNavigation variant="light" destination="santa-catarina" />

      {/* Text Block Header */}
      <section className="experiences-header-section" data-scroll-section>
        <div className="experiences-header-content">
          <h1>{t('properties/santa-catarina:experiences.pageTitle')}</h1>
          <p>
            {t('properties/santa-catarina:experiences.pageDescription')}
          </p>
        </div>
      </section>

      {/* Full Width Hero Image */}
      <section className="experiences-hero-image" data-scroll-section>
        <div 
          className="hero-image-container"
          style={{ backgroundImage: `url('/assets/experiencepagebreak.jpg')` }}
        />
      </section>

      {/* Guide/Experiences Section - 3x2 Grid */}
      <section className="experiences-guide-section" data-scroll-section>
        <div className="guide-section-container">
          {/* Filters and Guide Button */}
          <div className="guide-section-header">
            <div className="filter-buttons">
              <button className="filter-button active">
                <span>{t('properties/santa-catarina:experiences.filters.location')}</span>
              </button>
              <button className="filter-button">
                <span>{t('properties/santa-catarina:experiences.filters.type')}</span>
              </button>
            </div>
            <button className="guide-button">
              <span>{t('properties/santa-catarina:experiences.filters.ourGuides')}</span>
            </button>
          </div>

          <div className="experiences-grid">
            {experiences.map((experience) => (
              <div key={experience.id} className="experience-card">
                <div className="experience-image">
                  <img src={experience.image} alt={experience.title} />
                </div>
                <div className="experience-content">
                  <h3>{experience.title}</h3>
                  <span className="experience-location">{experience.location}</span>
                  <p>{experience.description}</p>
                  <button className="contact-link">
                    {t('properties/santa-catarina:experiences.contactUs')}
                    <svg className="underline" viewBox="0 0 78 2" fill="none">
                      <line y1="1" x2="78" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="guide-pagination">
            <button className="pagination-button active">{t('properties/santa-catarina:experiences.pagination.page1')}</button>
            <button className="pagination-button">{t('properties/santa-catarina:experiences.pagination.page2')}</button>
            <button className="pagination-button">{t('properties/santa-catarina:experiences.pagination.page3')}</button>
            <button className="pagination-button">{t('properties/santa-catarina:experiences.pagination.page4')}</button>
          </div>
        </div>
      </section>

      {/* Team Header + Body */}
      <section className="experiences-team-section" data-scroll-section>
        <div className="team-section-container">
          <div className="team-header">
            <h2>{t('properties/santa-catarina:experiences.team.sectionTitle')}</h2>
            <p>
              {t('properties/santa-catarina:experiences.team.sectionDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Team Carousel */}
      <section className="experiences-team-carousel" data-scroll-section>
        <div className="team-carousel-wrapper">
          <div className="team-carousel-container" id="team-carousel">
            <div className="team-members-scroll">
              {teamMembers.map((member) => (
                <div key={member.id} className="team-member-card">
                  <div className="member-image">
                    {member.image ? (
                      <img src={member.image} alt={member.name} />
                    ) : (
                      <div className="placeholder-image" />
                    )}
                  </div>
                  <div className="member-content">
                    <h3>{member.name}</h3>
                    <span className="member-role">{member.role}</span>
                    <p>{member.description}</p>
                    <button className="read-more-link">
                      {t('properties/santa-catarina:experiences.team.readMore')}
                      <svg className="underline" viewBox="0 0 76 2" fill="none">
                        <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button className="carousel-nav-arrow" aria-label="Next">
              <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                <path d="M22 14L37 29L22 44" stroke="#49413d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Call to Action Full Image */}
      <section className="experiences-cta-section" data-scroll-section>
        <div 
          className="cta-image-container"
          style={{ backgroundImage: `url('/assets/fullviewportatacama.jpg')` }}
        />
      </section>

      {/* Instagram Gallery */}
      <div className="instagram-section-wrapper">
        <InstagramSection />
      </div>

      {/* Footer - Santa Catarina variant */}
      <Footer variant="santa-catarina" />
    </div>
  );
};

export default ExperiencesSantaCatarina;